import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAccountId, selectAuthenticatedUser, selectAuthenticatedUserToken, selectAuthErrors } from '../../store/auth/authSlice'
import { useCreateFactorMutation, useDeleteFactorMutation, useLazyListFactorsQuery, useUpdateFactorMutation } from '../../store/mfa/mfa'
import { MFAChallengeResponse, MFAFactor, MFAFactorCreateRequest, MFAFactorCreateRequestWithChallengeResponse } from '../../models/MFA'
import {
  selectCreateRequest,
  selectFactorIdToDelete,
  selectFactorToUpdate,
  selectMFAFlow,
  toClaimCodePage,
  toClaimCodePageForCreate,
  toSetupFactorPage,
} from '../../store/mfa/mfaSlice'
import React, { useEffect, useState } from 'react'
import { loginWithMFATokenV2 } from '../../store/auth/authActions'
import { Token } from '../../common/token'

const getDefaultFactor = (factors: MFAFactor[]): MFAFactor => {
  return factors.find((factor) => factor.default) ?? factors[0]
}

export const useMFAFlow = () => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const flow = useAppSelector(selectMFAFlow)
  const token = useAppSelector(selectAuthenticatedUserToken)
  const storedCreateRequest = useAppSelector(selectCreateRequest)
  const factorToUpdate = useAppSelector(selectFactorToUpdate)
  const factorIdToDelete = useAppSelector(selectFactorIdToDelete)
  const [loading, setLoading] = useState(false)
  const [listFactors, { isFetching: isListFactorsLoading }] = useLazyListFactorsQuery()
  const [createFactor, { error: createFactorError, isLoading: isCreateFactorLoading }] = useCreateFactorMutation()
  const [updateFactor, { error: updateFactorError, isLoading: isUpdateFactorLoading }] = useUpdateFactorMutation()
  const [deleteFactor, { error: deleteFactorError, isLoading: isDeleteFactorLoading }] = useDeleteFactorMutation()
  const [errorMessage, setErrorMessage] = React.useState<string>('')
  const [flowDisplayText, setFlowDisplayText] = React.useState<string>('')
  const loginErrors = useAppSelector(selectAuthErrors)

  useEffect(() => {
    setErrorMessage(loginErrors?.[0]?.message)
  }, [loginErrors])

  useEffect(() => {
    // @ts-ignore
    setErrorMessage(createFactorError?.data?.errors?.[0]?.message || '')
  }, [createFactorError])

  useEffect(() => {
    // @ts-ignore
    setErrorMessage(updateFactorError?.data?.errors?.[0]?.message || '')
  }, [updateFactorError])

  useEffect(() => {
    // @ts-ignore
    setErrorMessage(deleteFactorError?.data?.errors?.[0]?.message || '')
  }, [deleteFactorError])

  useEffect(() => {
    setLoading(isListFactorsLoading || isCreateFactorLoading || isUpdateFactorLoading || isDeleteFactorLoading)
  }, [isListFactorsLoading, isCreateFactorLoading, isUpdateFactorLoading, isDeleteFactorLoading])

  useEffect(() => {
    switch (flow) {
      case 'login':
      case 'create':
      case 'update':
        setFlowDisplayText('Done')
        break
      case 'delete':
        setFlowDisplayText('Delete')
        break
    }
  }, [flow])

  const loginWithChallengeResponse = async (challengeResponse: MFAChallengeResponse) => {
    if (!token) {
      console.error('No token available')
      return
    }
    setLoading(true)
    try {
      await dispatch(loginWithMFATokenV2({ token: Token.fromSerialized(token).accessToken, challengeResponse }))
    } finally {
      setLoading(false)
    }
  }

  const createFactorWithRequest = async (createRequest: MFAFactorCreateRequestWithChallengeResponse) => {
    const { data: createdFactor } = await createFactor({
      accountId,
      request: createRequest,
    })
    if (createdFactor) {
      dispatch(toSetupFactorPage(createdFactor))
    }
  }

  const createFactorOrValidate = async (factor: MFAFactorCreateRequest) => {
    const { data: factors } = await listFactors({ accountId })
    if (factors && factors.length > 0) {
      dispatch(toClaimCodePageForCreate({ createRequest: factor, factorToChallenge: getDefaultFactor(factors) }))
    } else {
      await createFactorWithRequest({ factor })
    }
  }

  const createFactorWithChallengeResponse = async (challengeResponse: MFAChallengeResponse) => {
    if (storedCreateRequest) {
      await createFactorWithRequest({ factor: storedCreateRequest, challengeResponse })
    }
  }

  const updateFactorWithChallengeResponse = async (challengeResponse: MFAChallengeResponse) => {
    if (factorToUpdate && factorToUpdate.type === 'sms') {
      await updateFactor({
        accountId,
        factorId: factorToUpdate.id,
        request: { update: { phoneNumber: factorToUpdate.phoneNumber }, challengeResponse },
      }).unwrap()
    }
  }

  const startDeleteFactor = async () => {
    const { data: factors } = await listFactors({ accountId })
    if (factors) {
      dispatch(toClaimCodePage(getDefaultFactor(factors)))
    }
  }

  const deleteFactorWithChallengeResponse = async (challengeResponse: MFAChallengeResponse) => {
    if (factorIdToDelete) {
      await deleteFactor({ accountId, factorId: factorIdToDelete, challengeResponse }).unwrap()
    }
  }

  const submitChallengeResponse = async (challengeResponse: MFAChallengeResponse, onSuccess?: () => void) => {
    switch (flow) {
      case 'login':
        await loginWithChallengeResponse(challengeResponse)
        break
      case 'create':
        await createFactorWithChallengeResponse(challengeResponse)
        break
      case 'update':
        try {
          await updateFactorWithChallengeResponse(challengeResponse)
          onSuccess && onSuccess()
        } catch (e) {}
        break
      case 'delete':
        try {
          await deleteFactorWithChallengeResponse(challengeResponse)
          onSuccess && onSuccess()
        } catch (e) {}
        break
    }
  }

  return {
    errorMessage,
    isLoading: loading,
    flowDisplayText,
    loginWithChallengeResponse,
    createFactorOrValidate,
    startDeleteFactor,
    submitChallengeResponse,
  }
}
